import {useMemo, useState} from "react";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {orderByAscending, useAsync2, useAsyncAction} from "nate-react-api-helpers";
import useWindowSize from "react-use/lib/useWindowSize";
import {
    Button,
    DialogTitle,
    Grid, IconButton,
    List,
    ListItemButton, ListItemText, MenuItem, Select,
    TextField
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {FileItemSelection} from "./ProductCatalogLookup";
import {useFilePicker} from "./FilePicker";
import {grey} from "@mui/material/colors";
import {api} from "../../../../api/API";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {FileEditDialog} from "./FileEditDialog";
import {getAll} from "../../../logistics/routes/useGetAll";
import {ManufacturerSelector} from "../../../settings/products/ManufacturerSelector";

export function FileLookup<T extends FileItemSelection>(props: {
    title: string;
    manufacturer?: number;
    category?: number;
    uploadFx(input: {
        file: File;
        manufacturer: number
    }): Promise<T>;
    renameFileFx(input: T): Promise<any>;
    list(input: {
        manufacturer?: number;
        category?: number;
        include?: number;
    }): Promise<T[]>;
    fileLink(id: number): string
    onDone(value: T | null): Promise<void>;
    value: FileItemSelection | null;
    placeholder: string;
}) {
    const [category, setCategory] = useState<number | undefined>(props.category);

    const filePicker = useFilePicker({
        accept: ["application/pdf", "image/*"],
    });
    const uploadFx = useSyncedRef(props.uploadFx);

    const action = useAsyncAction(async (file) => {
        if(!props.manufacturer) throw new Error("Fill out manufacturer first");
        const catalog = await uploadFx.current({
            file: file,
            manufacturer: props.manufacturer,
        });

        setSelected(catalog);
        setPages("")
        fileList.reload()
    }, [filePicker, props.manufacturer]);

    const onDoneRef = useSyncedRef(props.onDone);
    const update = useAsyncAction((input) => onDoneRef.current(input), []);
    const [pages, setPages] = useState(props.value?.pages || "");

    const size = useWindowSize();

    const [selected, setSelected] = useState<FileItemSelection | null>(props.value);
    const [manufacturer, setManufacturer] = useState<number | null>(props.manufacturer || null);

    const fileList = useAsync2(input => {
        return props.list(input)
    }, {
        manufacturer: manufacturer || undefined,
        category: category,
        include: selected?.id
    }, [manufacturer, category, selected?.id])

    const categories = useAsync2(async (input) => {
        const hwList = await getAll(null, offset => api.products.categories({offset: offset, kind: "hardware"}), { useLazy: true })
        const d10List = await getAll(null, offset => api.products.categories({offset: offset, kind: "div-10"}), {useLazy: true})

        return [...hwList, ...d10List];
    }, {}, []);

    const categoryRawList = categories.asList;
    const categoryList = useMemo(() => {
        if(!categoryRawList) return [];

        const roots = categoryRawList.filter(v => !v.parentCategory);
        orderByAscending(roots, r => r.sortOrder || 0)

        const selected = categoryRawList.find(v => v.id === props.category);
        if(selected) {
            let cursor = selected;
            while(cursor.parentCategory) {
                cursor = categoryRawList.find(v => v.id === cursor.parentCategory) || cursor;
            }

            setCategory(cursor.id)
        }

        return roots;
    }, [props.category, categoryRawList])

    const [edit, setEdit] = useState<T>();
    const [search, setSearch] = useState("");

    let files = fileList.asList;
    if(search !== "") {
        files = files.filter(f => f.name.toLowerCase().startsWith(search.toLowerCase()))
    }

    const submit = () => {
        update.callback(Object.assign({}, selected, {
            pages: pages,
        }));
    };

    return (
        <>
            <Dialog2 open fullWidth maxWidth="lg" onClose={e => props.onDone(props.value as any)} onEnterPress={submit}>
                <DialogTitle>{props.title}</DialogTitle>
                <div style={{width: "100%", height: size.height * 0.75, borderTop: "1px solid " + grey["300"]}}>
                    <Grid container spacing={1} style={{height: "100%"}}>
                        <Grid item xs={3}
                              style={{display: "flex", flexDirection: "column", height: "100%", paddingLeft: 16}}>
                            <TextField label="Search" size="small" fullWidth value={search}
                                       onChange={e => setSearch(e.target.value)}/>
                            <div style={{height: 8}}/>
                            {props.category && categoryList.length > 0 &&
                                <Select size="small" fullWidth value={category?.toString() || "-"}>
                                    <MenuItem value={"-"} onClick={() => {
                                        setCategory(undefined)
                                        setSearch("");
                                    }}>All Categories</MenuItem>
                                    {categoryList.map(c =>
                                        <MenuItem key={c.id.toString()} onClick={() => {
                                            setCategory(c.id)
                                            setSearch("");
                                        }} value={c.id.toString()}>{c.name}</MenuItem>
                                    )}
                                </Select>}
                            <div style={{height: 8}}/>
                            {props.manufacturer && <ManufacturerSelector defaultCss value={manufacturer} onChange={setManufacturer}
                                                                         defaultValueTitle={"All Manufacturers"}/>}
                            <List dense style={{flex: 1, overflow: "auto"}}>
                                {fileList.LoadingOrErrorElement}
                                {files.map(r =>
                                    <ListItemButton
                                        style={{borderBottom: "1px solid " + grey["200"]}}
                                        selected={r.id === selected?.id} onClick={e => setSelected(r)}>

                                        <ListItemText primary={r.name}/>
                                        <IconButton onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setEdit(r);
                                        }}>
                                            <MoreVertIcon/>
                                        </IconButton>
                                    </ListItemButton>
                                )}
                            </List>
                            <div style={{
                                paddingLeft: 16,
                                display: "flex",
                                flexDirection: "column",
                            }}>
                                <TextField label="Pages"
                                           placeholder="1-5"
                                           helperText={"Use range (e.g. 1-5) or blank to include entire document"}
                                           value={pages}
                                           onChange={e => setPages(e.target.value)}
                                           size="small"
                                />
                                <div style={{width: 16}}/>
                                {action.LoadingElement}
                                <Button onClick={async () => {
                                    if (!props.manufacturer) return;
                                    const files = await filePicker();
                                    action.callback(files[0])
                                }}>
                                    Upload File
                                </Button>
                            </div>
                        </Grid>
                        <Grid item xs={9} style={{
                            display: "flex",
                            height: "100%"
                        }}>
                            {selected ? <iframe
                                src={props.fileLink(selected.id)}
                                style={{
                                    flex: 1,
                                }}
                            ></iframe> : <div style={{
                                backgroundColor: grey["100"],
                                flex: 1,
                                display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                                <div>
                                    Choose a {props.placeholder} on the right
                                </div>
                            </div>}
                        </Grid>
                    </Grid>
                </div>
                <SmoothActionBar
                    loading={update.loading}
                    error={update.error}
                    actionName="Update"
                    onCancel={() => props.onDone(props.value as any)}
                    onSubmit={submit}
                />
            </Dialog2>
            {edit && <FileEditDialog value={edit} onRename={props.renameFileFx} onClose={() => {
                setEdit(undefined);
                fileList.reload();
            }} />}
        </>
    )
}